import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box,
  TextField, Grid, Stack, Switch, FormControlLabel, FormControl,
  InputLabel, Select, MenuItem, CircularProgress, Typography, IconButton
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import axiosClient from '../../api/axiosClient';
import { getCategories } from '../../api/endpoints/categories';
import { uploadFile } from '../../api/endpoints/media';

const initialForm = {
  name: '', shortDescription: '', description: '', category: '',
  price: '', comparePrice: '', stock: '0', weight: '', dimensions: '',
  material: '', isActive: true, isFeatured: false,
};

export default function AddProductModal({ open, onClose, onSaved }) {
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(initialForm);
      setImageFile(null);
      setImagePreview(null);
      fetchCategories();
    }
  }, [open]);

  const fetchCategories = async () => {
    try {
      const res = await getCategories({ type: 'product' });
      setCategories(res.data.data || []);
    } catch { /* ignore */ }
  };

  const handleChange = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Product name is required'); return; }
    if (!form.price && form.price !== 0) { toast.error('Price is required'); return; }
    setSaving(true);
    try {
      let imageUrl = '';
      if (imageFile) {
        const uploadRes = await uploadFile(imageFile);
        imageUrl = uploadRes.data.data?.url || '';
      }
      const payload = {
        name: form.name,
        shortDescription: form.shortDescription,
        description: form.description,
        category: form.category || undefined,
        price: Number(form.price),
        comparePrice: form.comparePrice ? Number(form.comparePrice) : undefined,
        stock: Number(form.stock) || 0,
        weight: form.weight || undefined,
        dimensions: form.dimensions || undefined,
        material: form.material || undefined,
        isActive: form.isActive,
        isFeatured: form.isFeatured,
      };
      if (imageUrl) payload.images = [imageUrl];
      await axiosClient.post('/admin/shop/products', payload);
      toast.success('Product created');
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create product');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight={700}>Add Product</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField label="Product Name" value={form.name} onChange={handleChange('name')} fullWidth required size="small" />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Short Description" value={form.shortDescription} onChange={handleChange('shortDescription')} fullWidth size="small" />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Description" value={form.description} onChange={handleChange('description')} fullWidth multiline rows={3} size="small" />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select value={form.category} label="Category" onChange={handleChange('category')}>
                <MenuItem value="">None</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <TextField label="Price (₹)" type="number" value={form.price} onChange={handleChange('price')} fullWidth required size="small" />
          </Grid>
          <Grid item xs={3}>
            <TextField label="Compare Price" type="number" value={form.comparePrice} onChange={handleChange('comparePrice')} fullWidth size="small" />
          </Grid>
          <Grid item xs={3}>
            <TextField label="Stock" type="number" value={form.stock} onChange={handleChange('stock')} fullWidth size="small" />
          </Grid>
          <Grid item xs={3}>
            <TextField label="Weight" value={form.weight} onChange={handleChange('weight')} fullWidth size="small" />
          </Grid>
          <Grid item xs={3}>
            <TextField label="Dimensions" value={form.dimensions} onChange={handleChange('dimensions')} fullWidth size="small" placeholder="e.g. 10x8x2 cm" />
          </Grid>
          <Grid item xs={3}>
            <TextField label="Material" value={form.material} onChange={handleChange('material')} fullWidth size="small" />
          </Grid>
          <Grid item xs={6}>
            <Stack direction="row" spacing={2}>
              <FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))} />} label="Active" />
              <FormControlLabel control={<Switch checked={form.isFeatured} onChange={(e) => setForm((p) => ({ ...p, isFeatured: e.target.checked }))} />} label="Featured" />
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>Product Image</Typography>
            <Button variant="outlined" component="label" startIcon={<AddPhotoAlternateIcon />}>
              {imageFile ? 'Change Image' : 'Upload Image'}
              <input type="file" hidden accept="image/*" onChange={handleImageSelect} />
            </Button>
            {imagePreview && (
              <Box sx={{ mt: 1, position: 'relative', display: 'inline-block' }}>
                <img src={imagePreview} alt="Preview" style={{ maxWidth: 200, maxHeight: 150, borderRadius: 8 }} />
                <IconButton size="small" sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'white' }} onClick={() => { setImageFile(null); setImagePreview(null); }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Create Product'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
